/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import {
	Image,
	Box,
	Drawer as NativeDrawer,
	Text,
	Button,
	Stack,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

// Components
import { Tooltip } from '@components/atoms/Tooltip';

// Style
import classes from './DrawerSiteShowMore.module.css';

// Utils
import { truncateString } from '@utils/functions';

// Icons
import SiteLogo from '@assets/images/site-logo.svg';

import { IconEdit, IconTrash } from '@tabler/icons-react';

// I18n
import { useTranslation } from 'react-i18next';

// Type
import { TParsedSite } from '../../types';
import { useAuthorization } from '@lib/authorization';
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */

type DrawerMeterPointShowMoreProps = {
	onClose: () => void;
	handledelete?: () => void;
	handleupdate?: () => void;
	opened: boolean;
	data: Partial<TParsedSite>;
	position: 'right' | 'left';
	fromstepper?: boolean;
};
/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const DrawerSiteShowMore = (
	props: DrawerMeterPointShowMoreProps
) => {
	const {
		opened,
		onClose,
		handledelete,
		fromstepper,
		data: site,
		handleupdate,
		...restProps
	} = props;

	const {
		organizationName,
		producerName,
		projectName,
		unit,
		creationDate,
		lastConversionFactor,
		minValueConverted,
		maxValueConverted,
		meterPointsCount,
		createdAt,
	} = site;
	/* ********************************  HOOKS  ********************************* */

	const { t } = useTranslation();
	/* ******************************** Media Query ******************************* */
	const isBigScreen = useMediaQuery('(min-width: 120em)');

	const { checkAcl, checkRole } = useAuthorization();
	/* ******************************** RENDERING ******************************* */
	if (site)
		return (
			<NativeDrawer
				onClose={onClose}
				opened={opened}
				classNames={classes}
				overlayProps={{
					backgroundOpacity: 0.2,
					blur: 0.5,
				}}
				{...restProps}
			>
				{/* Header */}
				<Box className={classes.customHeader}>
					<Box className={classes.containerLogo}>
						<Image
							className={classes.logo}
							src={SiteLogo}
						/>
					</Box>
					<Box className={classes.containerTitle}>
						<Text className={classes.customSubTitle}>
							{t('global.site')}
						</Text>
						{props.data.project && (
							<Text className={classes.customTitle}>
								{props.data.name}
							</Text>
						)}
					</Box>
				</Box>
				{/* Body */}

				<Box className={classes.bodyDrawer}>
					{organizationName && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.organization')}
							</Text>
							<Tooltip
								label={organizationName}
								isActive={organizationName.length > 20}
							>
								<Text className={classes.value}>
									{truncateString(organizationName, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}

					{producerName && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.producer')}
							</Text>
							<Tooltip
								label={producerName}
								isActive={producerName.length > 20}
							>
								<Text className={classes.value}>
									{truncateString(producerName, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}

					{projectName && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.project')}
							</Text>
							<Tooltip
								label={projectName}
								isActive={projectName.length > 20}
							>
								<Text className={classes.value}>
									{truncateString(projectName, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}

					{meterPointsCount !== undefined && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.meterpointCount')}
							</Text>
							<Text className={classes.value}>
								{meterPointsCount}
							</Text>
						</Box>
					)}

					{lastConversionFactor && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('sites.lastConversionFactor')}
							</Text>
							<Text className={classes.value}>
								{lastConversionFactor}
							</Text>
						</Box>
					)}

					{minValueConverted && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('sites.minValueProduction')}
							</Text>
							<Text className={classes.value}>
								{minValueConverted}
							</Text>
						</Box>
					)}

					{maxValueConverted && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('sites.maxValueProduction')}
							</Text>
							<Text className={classes.value}>
								{maxValueConverted}
							</Text>
						</Box>
					)}

					{unit && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.unit')}
							</Text>
							<Tooltip
								label={unit}
								isActive={unit.length > 20}
							>
								<Text className={classes.value}>
									{truncateString(unit, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}

					{createdAt && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.creationDate')}
							</Text>
							<Tooltip
								label={creationDate}
								isActive={createdAt.length > 20}
							>
								<Text className={classes.value}>
									{truncateString(createdAt, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}
				</Box>

				{/* Footer */}
				{!fromstepper && (
					<Stack gap={'10px'} className={classes.footer}>
						{checkAcl(ACLS.SITES.UPDATE) &&
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							]) && (
								<Button
									onClick={handleupdate}
									leftSection={<IconEdit size={16} />}
									size={isBigScreen ? 'md' : 'sm'}
									fullWidth
									classNames={{
										root: classes.customDeleteButton,
									}}
								>
									{t('sites.updateSite')}
								</Button>
							)}
						{checkAcl(ACLS.SITES.DELETE) &&
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							]) && (
								<Button
									disabled
									onClick={handledelete}
									leftSection={<IconTrash size={16} />}
									size={isBigScreen ? 'md' : 'sm'}
									fullWidth
									classNames={{
										root: classes.customDeleteButton,
									}}
								>
									{t('sites.deleteSite')}
								</Button>
							)}
					</Stack>
				)}
			</NativeDrawer>
		);
};
